import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Car, MapPin, User, CheckCircle2, Phone, MessageSquareText, Wrench } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";
import { api } from "../lib/api";
import { SITE } from "../config/site";

const YEARS = Array.from({ length: 33 }, (_, i) => String(2027 - i));

const SERVICE_OPTIONS = [
  { id: "brakes", label: "Brake Service" },
  { id: "wheel-bearing", label: "Wheel Bearing / Hub" },
  { id: "suspension", label: "Suspension Repair" },
  { id: "alternator", label: "Alternator" },
  { id: "starter", label: "Starter" },
  { id: "tune-up", label: "Tune-Up / Maintenance" },
  { id: "other", label: "Something Else" },
];

const BRAKE_OPTIONS = [
  { id: "front", label: "Front" },
  { id: "rear", label: "Rear" },
  { id: "both", label: "Both" },
  { id: "inspection", label: "Not Sure" },
];

const inputCls =
  "min-h-[52px] w-full border border-edge bg-slate2 px-4 text-base text-slate-100 placeholder:text-slate-500 focus:border-amber2 focus:outline-none";

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="mb-1.5 block font-mono text-xs uppercase tracking-widest text-slate-400">{label}</span>
      {children}
    </label>
  );
}

export default function QuotePage() {
  const [searchParams] = useSearchParams();
  const initialService = SERVICE_OPTIONS.some((s) => s.id === searchParams.get("service"))
    ? searchParams.get("service")
    : "brakes";
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [quoteId, setQuoteId] = useState("");
  const [form, setForm] = useState({
    service: initialService,
    year: YEARS.includes(searchParams.get("year")) ? searchParams.get("year") : "",
    make: searchParams.get("make") || "",
    model: searchParams.get("model") || "",
    engine: "", brakes: "",
    description: (searchParams.get("description") || "").slice(0, 1000),
    zip_code: /^[0-9]{5}$/.test(searchParams.get("zip") || "") ? searchParams.get("zip") : "",
    name: "", phone: "", email: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.service === "brakes" && !form.brakes) {
      toast.error("Please choose front, rear or both brakes.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, brakes: form.service === "brakes" ? form.brakes : null };
      const res = await api.submitQuote(payload);
      toast.success("Quote request received!");
      setQuoteId(res.id);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call or text us instead.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <SEO
          title="Quote Request Received | My Mobile Mechanic"
          description="Your quote request was received. Call or text (978) 967-9860 to book your mobile repair."
          path="/get-a-brake-quote"
        />
        <section className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24">
          <Reveal>
            <div data-testid="quote-success-panel" className="border border-edge bg-slate2 p-8 text-center sm:p-10">
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
              <h1 className="mt-5 font-display text-4xl font-black uppercase tracking-tight text-slate-50">
                Quote Request Received
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Reference <span data-testid="quote-reference" className="font-mono font-bold text-amber2">{quoteId.slice(-6).toUpperCase()}</span> — a confirmation email is on its way to you. Now call or text to lock in your day and time:
              </p>
              <p data-testid="quote-success-phone" className="mt-5 font-display text-4xl font-black tracking-tight text-crimson">
                {SITE.phone}
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <a
                  href={SITE.phoneHref}
                  data-testid="quote-success-call-button"
                  className="flex min-h-[56px] items-center justify-center gap-2 bg-crimson font-display text-xl font-bold uppercase text-white transition-colors hover:bg-crimsondark"
                >
                  <Phone className="h-5 w-5" /> Call Now
                </a>
                <a
                  href={SITE.smsHref}
                  data-testid="quote-success-text-button"
                  className="flex min-h-[56px] items-center justify-center gap-2 border-2 border-crimson font-display text-xl font-bold uppercase text-crimson transition-colors hover:bg-crimson hover:text-white"
                >
                  <MessageSquareText className="h-5 w-5" /> Text Us
                </a>
              </div>
              <p className="mt-5 text-xs text-slate-500">{SITE.hours} · {SITE.closedNote}</p>
            </div>
          </Reveal>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Get a Quote | My Mobile Mechanic — Mobile Auto Repair"
        description="Get an exact quote in two minutes — brakes, wheel bearings, suspension, alternators, starters and maintenance at your home or workplace. Then call or text to book."
        path="/get-a-brake-quote"
      />
      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-20">
        <Reveal>
          <Eyebrow>Free Two-Minute Quote</Eyebrow>
          <h1 className="mt-3 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-slate-50 sm:text-5xl">
            Get a <span className="text-crimson">Quote</span>
          </h1>
          <p className="mt-4 text-base text-slate-400">
            Tell us about your vehicle and what it needs — we'll give you an exact, no-surprise price. Then call or text to book.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form data-testid="quote-form" onSubmit={submit} className="mt-10 space-y-8">
            <fieldset className="border border-edge bg-slate2/50 p-5 sm:p-7">
              <legend className="flex items-center gap-2 px-2 font-display text-xl font-bold uppercase text-amber2">
                <Wrench className="h-5 w-5" /> Service Needed
              </legend>
              <Field label="What does your vehicle need? *" testid="quote-service-field">
                <select data-testid="quote-service-select" required value={form.service} onChange={set("service")} className={inputCls}>
                  {SERVICE_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </Field>
              {form.service === "brakes" && (
                <div className="mt-5">
                  <span className="mb-2 block font-mono text-xs uppercase tracking-widest text-slate-400">
                    Which brakes? *
                  </span>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {BRAKE_OPTIONS.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        data-testid={`quote-brakes-${b.id}-button`}
                        onClick={() => setForm({ ...form, brakes: b.id })}
                        className={`min-h-[52px] border px-3 font-display text-base font-bold uppercase transition-colors ${
                          form.brakes === b.id
                            ? "border-crimson bg-crimson text-white"
                            : "border-edge bg-slate2 text-slate-300 hover:border-edgehi"
                        }`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-5">
                <Field label="Describe the problem (symptoms, noises, warning lights…)" testid="quote-description-field">
                  <textarea
                    data-testid="quote-description-input"
                    rows={3}
                    value={form.description}
                    onChange={set("description")}
                    placeholder="e.g. Grinding noise when braking, worse when turning left"
                    className={`${inputCls} py-3`}
                  />
                </Field>
              </div>
            </fieldset>

            <fieldset className="border border-edge bg-slate2/50 p-5 sm:p-7">
              <legend className="flex items-center gap-2 px-2 font-display text-xl font-bold uppercase text-amber2">
                <Car className="h-5 w-5" /> Your Vehicle
              </legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Year *" testid="quote-year-field">
                  <select data-testid="quote-year-select" required value={form.year} onChange={set("year")} className={inputCls}>
                    <option value="">Select year</option>
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </Field>
                <Field label="Make *" testid="quote-make-field">
                  <input data-testid="quote-make-input" required value={form.make} onChange={set("make")} placeholder="Honda" className={inputCls} />
                </Field>
                <Field label="Model *" testid="quote-model-field">
                  <input data-testid="quote-model-input" required value={form.model} onChange={set("model")} placeholder="Civic" className={inputCls} />
                </Field>
                <Field label="Engine (if known)" testid="quote-engine-field">
                  <input data-testid="quote-engine-input" value={form.engine} onChange={set("engine")} placeholder="2.0L 4-cyl" className={inputCls} />
                </Field>
              </div>
            </fieldset>

            <fieldset className="border border-edge bg-slate2/50 p-5 sm:p-7">
              <legend className="flex items-center gap-2 px-2 font-display text-xl font-bold uppercase text-amber2">
                <MapPin className="h-5 w-5" /> Your Location
              </legend>
              <Field label="ZIP Code *" testid="quote-zip-field">
                <input
                  data-testid="quote-zip-input"
                  required
                  inputMode="numeric"
                  pattern="[0-9]{5}"
                  title="5-digit ZIP code"
                  value={form.zip_code}
                  onChange={set("zip_code")}
                  placeholder="01826"
                  className={inputCls}
                />
              </Field>
            </fieldset>

            <fieldset className="border border-edge bg-slate2/50 p-5 sm:p-7">
              <legend className="flex items-center gap-2 px-2 font-display text-xl font-bold uppercase text-amber2">
                <User className="h-5 w-5" /> Your Details
              </legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name *" testid="quote-name-field">
                  <input data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </Field>
                <Field label="Phone *" testid="quote-phone-field">
                  <input data-testid="quote-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(978) 555-0100" className={inputCls} />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Email *" testid="quote-email-field">
                    <input data-testid="quote-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                  </Field>
                </div>
              </div>
            </fieldset>

            <button
              type="submit"
              data-testid="quote-submit-button"
              disabled={busy}
              className="group flex min-h-[56px] w-full items-center justify-center gap-2 bg-crimson font-display text-xl font-bold uppercase tracking-wide text-white transition-colors hover:bg-crimsondark disabled:opacity-60"
            >
              {busy ? "Sending…" : "Get My Quote"}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-center text-xs text-slate-500">
              No payment due today. After you submit, just call or text us to book.
            </p>
          </form>
        </Reveal>
      </section>
    </>
  );
}
